import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar, Clock, MapPin, Users, Plus, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatError } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ShareButtons from '../components/ShareButtons';
import RichTextEditor from '../components/RichTextEditor';
import { stripHtml } from '../components/RichContent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function EventCard({ event, user, onJoin, onDelete }) {
  const isPast = new Date(`${event.date}T${event.time || '23:59'}`) < new Date();
  return (
    <div className="card-hover p-5" data-testid={`event-card-${event._id}`}>
      {event.image_url && (
        <div className="aspect-video rounded-xl overflow-hidden mb-4 -mx-5 -mt-5">
          <img src={event.image_url.startsWith('http') ? event.image_url : `${API}/files/${event.image_url}`}
            alt={event.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-slate-800 text-lg leading-tight">{event.title}</h3>
        {(user?._id === event.organizer_id || user?.role === 'admin') && (
          <button onClick={() => onDelete(event._id)} className="text-slate-300 hover:text-red-500 shrink-0 transition-colors" data-testid={`event-delete-${event._id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="space-y-1.5 mt-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Calendar className="w-4 h-4 text-emerald-500 shrink-0" />
          <span>{event.date} {event.time && `· ${event.time}`}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <MapPin className="w-4 h-4 text-emerald-500 shrink-0" />
          <span>{event.location}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Users className="w-4 h-4 text-emerald-500 shrink-0" />
          <span>{event.participant_count || 0} katılımcı</span>
        </div>
      </div>
      {event.description && (
        <p className="text-sm text-slate-600 line-clamp-2 mb-4">{stripHtml(event.description)}</p>
      )}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-400">Düzenleyen: {event.organizer_display_name || event.organizer_username}</div>
        {user && !isPast && (
          <Button
            size="sm"
            onClick={() => onJoin(event._id)}
            className={event.user_joined ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            data-testid={`event-join-btn-${event._id}`}
          >
            {event.user_joined ? 'Ayrıl' : 'Katıl'}
          </Button>
        )}
        {isPast && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-full">Geçmiş</span>}
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <ShareButtons
          size="xs"
          url={`${window.location.origin}/etkinlikler#${event._id}`}
          title={event.title}
          text={`${event.title} · ${event.date} · ${event.location} | MiniOto`}
        />
      </div>
    </div>
  );
}

export default function EventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', location: '', image_url: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/events`);
      const eventsData = r.data.events || [];

      // Check join status for each event if user is logged in
      if (user) {
        const withJoinStatus = await Promise.all(eventsData.map(async (ev) => {
          try {
            const detail = await axios.get(`${API}/events/${ev._id}`, { withCredentials: true });
            return { ...ev, user_joined: detail.data.user_joined };
          } catch {
            return ev;
          }
        }));
        setEvents(withJoinStatus);
      } else {
        setEvents(eventsData);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, [user?._id]);

  const handleJoin = async (eventId) => {
    if (!user) { navigate('/giris'); return; }
    try {
      const r = await axios.post(`${API}/events/${eventId}/join`, {}, { withCredentials: true });
      setEvents(prev => prev.map(ev =>
        ev._id === eventId
          ? { ...ev, user_joined: r.data.joined, participant_count: ev.participant_count + (r.data.joined ? 1 : -1) }
          : ev
      ));
      toast.success(r.data.joined ? 'Etkinliğe katıldınız!' : 'Etkinlikten ayrıldınız');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Etkinliği silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, { withCredentials: true });
      setEvents(prev => prev.filter(ev => ev._id !== eventId));
      toast.success('Etkinlik silindi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.location) {
      toast.error('Başlık, tarih ve konum zorunludur');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/events`, form, { withCredentials: true });
      setEvents(prev => [r.data.event, ...prev]);
      setShowCreate(false);
      setForm({ title: '', description: '', date: '', time: '', location: '', image_url: '' });
      toast.success('Etkinlik oluşturuldu!');
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const upcoming = events.filter(ev => new Date(`${ev.date}T${ev.time || '23:59'}`) >= new Date());
  const past = events.filter(ev => new Date(`${ev.date}T${ev.time || '23:59'}`) < new Date());

  // Build Event JSON-LD for upcoming events only (no fake prices)
  const eventSchemas = upcoming.map(ev => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: ev.title,
      startDate: ev.time ? `${ev.date}T${ev.time}` : ev.date,
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      ...(ev.location ? { location: { '@type': 'Place', name: ev.location } } : {}),
      ...(ev.description ? { description: stripHtml(ev.description) } : {}),
      organizer: { '@type': 'Person', name: ev.organizer_name || 'MiniOto' },
    };
    return schema;
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title="Etkinlikler · Buluşmalar ve Sürüşler"
        description="L6e ve L7e sahipleri için düzenlenen buluşmalar, sürüş etkinlikleri ve topluluk programları. Yaklaşan etkinliklere katıl veya kendi etkinliğini oluştur."
        jsonLd={eventSchemas}
      />
      <Breadcrumbs items={[{ name: 'Etkinlikler' }]} />
      <AdSlot placement="events_top" className="mb-6" />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Etkinlikler</h1>
          <p className="text-slate-500 mt-1">Buluşmalar, sürüş etkinlikleri ve daha fazlası</p>
        </div>
        {user && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-event-btn">
                <Plus className="w-4 h-4 mr-2" /> Etkinlik Oluştur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Yeni Etkinlik</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div>
                  <Label>Etkinlik Adı</Label>
                  <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Etkinlik adı..." className="mt-1" data-testid="event-title-input" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tarih</Label>
                    <Input type="date" value={form.date} onChange={e => setForm(p => ({...p, date: e.target.value}))} className="mt-1" data-testid="event-date-input" required />
                  </div>
                  <div>
                    <Label>Saat</Label>
                    <Input type="time" value={form.time} onChange={e => setForm(p => ({...p, time: e.target.value}))} className="mt-1" data-testid="event-time-input" />
                  </div>
                </div>
                <div>
                  <Label>Konum</Label>
                  <Input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="Şehir, mekân..." className="mt-1" data-testid="event-location-input" required />
                </div>
                <div>
                  <Label>Açıklama</Label>
                  <div className="mt-1" data-testid="event-desc-input">
                    <RichTextEditor
                      value={form.description}
                      onChange={v => setForm(p => ({...p, description: v}))}
                      placeholder="Etkinlik hakkında bilgi..."
                      minHeight={160}
                    />
                  </div>
                </div>
                <div>
                  <Label>Görsel URL (isteğe bağlı)</Label>
                  <Input value={form.image_url} onChange={e => setForm(p => ({...p, image_url: e.target.value}))} placeholder="https://..." className="mt-1" data-testid="event-image-input" />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>İptal</Button>
                  <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="event-submit-btn">
                    {submitting ? 'Oluşturuluyor...' : 'Etkinlik Oluştur'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="mb-10">
              <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Yaklaşan Etkinlikler</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {upcoming.map(ev => (
                  <EventCard key={ev._id} event={ev} user={user} onJoin={handleJoin} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Geçmiş Etkinlikler</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 opacity-60">
                {past.map(ev => (
                  <EventCard key={ev._id} event={ev} user={user} onJoin={handleJoin} onDelete={handleDelete} />
                ))}
              </div>
            </div>
          )}

          {events.length === 0 && (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Henüz etkinlik yok</p>
              {user && (
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowCreate(true)}>İlk Etkinliği Oluştur</Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
